import React, { useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { COLORS, FONT } from "../../shared/styles";
import { buildBehaviourPatch } from "./constants";
import PreChatFormBuilder from "./PreChatFormBuilder";
import SettingsToggle from "./SettingsToggle";

const BEHAVIOUR_OPTIONS = [
  {
    key: "showWhenOffline",
    title: "Show Widget When Offline",
    description: "Display the widget even when no agents are available",
  },
  {
    key: "preChatForm",
    title: "Pre-Chat Form",
    description: "Collect visitor information before starting a chat",
  },
];

function BehaviourToggleCard({
  title,
  description,
  helperText,
  checked,
  disabled,
  onChange,
}) {
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "80px",
        borderRadius: "8px",
        border: `1px solid ${COLORS.border}`,
        background: COLORS.white,
        p: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <Box>
        <Typography
          sx={{
            color: COLORS.textPrimary,
            fontFamily: FONT.family,
            fontSize: "16px",
            fontWeight: 500,
            lineHeight: "22px",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            mt: "4px",
            color: COLORS.textSecondary,
            fontFamily: FONT.family,
            fontSize: "14px",
            fontWeight: 400,
            lineHeight: "18px",
          }}
        >
          {description}
        </Typography>
        {helperText ? (
          <Typography
            sx={{
              mt: "6px",
              color: COLORS.primary,
              fontFamily: FONT.family,
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "16px",
            }}
          >
            {helperText}
          </Typography>
        ) : null}
      </Box>
      <SettingsToggle
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        ariaLabel={title}
      />
    </Box>
  );
}

const START_CHAT_ALWAYS_OPTION = {
  key: "startChatAlways",
  title: "Start Chat Always",
  description:
    "When enabled, visitors can start a chat even when no agents are online. When disabled, visitors will only be able to submit a ticket.",
};

const ALLOW_GUEST_OPTION = {
  key: "allowGuest",
  title: "Allow Guest",
  description: "Let visitors start a chat without entering their details",
};

function BehaviourTab({ settings, onChange }) {
  const showWhenOfflineLocked = Boolean(settings.startChatAlways);

  const handleToggle = (key, nextValue) => {
    if (key === "showWhenOffline" && showWhenOfflineLocked) return;
    onChange(buildBehaviourPatch(key, nextValue));
  };

  const handlePreChatFieldsChange = useCallback(
    (preChatFields) => onChange({ preChatFields }),
    [onChange],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <BehaviourToggleCard
        key={START_CHAT_ALWAYS_OPTION.key}
        title={START_CHAT_ALWAYS_OPTION.title}
        description={START_CHAT_ALWAYS_OPTION.description}
        checked={settings.startChatAlways}
        onChange={(value) => handleToggle(START_CHAT_ALWAYS_OPTION.key, value)}
      />

      {BEHAVIOUR_OPTIONS.map((option) => {
        const isShowWhenOffline = option.key === "showWhenOffline";
        const disabled = isShowWhenOffline && showWhenOfflineLocked;

        return (
          <React.Fragment key={option.key}>
            <BehaviourToggleCard
              title={option.title}
              description={option.description}
              helperText={
                disabled
                  ? "Automatically enabled because Start Chat Always is active."
                  : undefined
              }
              checked={
                isShowWhenOffline && showWhenOfflineLocked
                  ? true
                  : settings[option.key]
              }
              disabled={disabled}
              onChange={(value) => handleToggle(option.key, value)}
            />

            {option.key === "preChatForm" && settings.preChatForm ? (
              <>
                <PreChatFormBuilder
                    fields={settings.preChatFields}
                    onChange={handlePreChatFieldsChange}
                />
                <BehaviourToggleCard
                  key={ALLOW_GUEST_OPTION.key}
                  title={ALLOW_GUEST_OPTION.title}
                  description={ALLOW_GUEST_OPTION.description}
                  checked={settings.allowGuest}
                  onChange={(value) => onChange({ allowGuest: value })}
                />
              </>
            ) : null}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

export default React.memo(BehaviourTab);
